import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ClientHeader from "../client/ClientHeader";
import ClientSidebar from "../client/ClientSidebar";
import ClientFooter from "../client/ClientFooter";

/**
 * Client Layout:
 * - Mobile-first responsive design.
 * - Sidebar hidden by default on mobile, shown as overlay when toggled.
 * - On desktop, sidebar is always visible and can be collapsed.
 */
const ClientLayout = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Close sidebar by default on mobile
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const sidebarWidth = sidebarOpen ? 256 : 80;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-white flex flex-col">
      {/* Sidebar (fixed + animated) */}
      <AnimatePresence>
        {(sidebarOpen || !isMobile) && (
          <motion.div
            initial={{ x: isMobile ? -256 : 0, width: isMobile ? 256 : sidebarWidth }}
            animate={{ x: 0, width: isMobile ? 256 : sidebarWidth }}
            exit={{ x: -256 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-y-0 left-0 z-30 bg-white shadow-xl border-r border-gray-100 overflow-hidden"
          >
            <ClientSidebar
              isOpen={isMobile ? true : sidebarOpen}
              onToggle={() => setSidebarOpen((s) => !s)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile overlay backdrop */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-20 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Main column */}
      <motion.div
        animate={{
          marginLeft: isMobile ? 0 : sidebarWidth,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="flex flex-col min-h-screen"
      >
        {/* Header */}
        <ClientHeader onToggleSidebar={() => setSidebarOpen((s) => !s)} />

        {/* Main Content Area */}
        <main className="flex-1 p-4 md:p-6 overflow-y-auto transition-all duration-300">
          {children}
        </main>

        {/* Footer */}
        <ClientFooter />
      </motion.div>
    </div>
  );
};

export default ClientLayout;
